import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Download, Share, Plus, SquarePlus } from "lucide-react";

function isIosDevice() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent.toLowerCase();
  const iDevice = /iphone|ipad|ipod/.test(ua);
  // iPadOS 13+ reports as Mac but is touch-capable
  const iPadOs = window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1;
  return iDevice || iPadOs;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.matchMedia?.("(display-mode: minimal-ui)")?.matches ||
    window.navigator.standalone === true
  );
}

/**
 * In-app PWA install affordance.
 * - Chromium/Android: captures `beforeinstallprompt` and fires the native install prompt.
 * - iOS Safari (no native prompt): opens an "Add to Home Screen" instructions dialog.
 * - Already installed (standalone): renders nothing.
 */
export default function InstallButton({ className = "", variant = "secondary", label = "Install app" }) {
  const [deferred, setDeferred] = useState(null);
  const [canPrompt, setCanPrompt] = useState(false);
  const [ios, setIos] = useState(false);
  const [standalone, setStandalone] = useState(false);
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    setIos(isIosDevice());
    setStandalone(isStandalone());

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setCanPrompt(true);
    };
    const onInstalled = () => {
      setCanPrompt(false);
      setDeferred(null);
      setStandalone(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Already installed → nothing to do.
  if (standalone) return null;

  // Show only when we can actually do something useful.
  const visible = canPrompt || ios;
  if (!visible) return null;

  const onClick = async () => {
    if (canPrompt && deferred) {
      deferred.prompt();
      try {
        await deferred.userChoice;
      } catch (e) {
        /* user dismissed */
      }
      setDeferred(null);
      setCanPrompt(false);
      return;
    }
    if (ios) setIosOpen(true);
  };

  return (
    <>
      <Button variant={variant} className={className} onClick={onClick} data-testid="pwa-install-button">
        <Download className="mr-2 h-4 w-4" /> {label}
      </Button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="pwa-ios-instructions">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Install Friday Conversations</DialogTitle>
            <DialogDescription>
              Add the app to your Home Screen for a full-screen, app-like experience — no App Store needed.
            </DialogDescription>
          </DialogHeader>
          <ol className="mt-2 space-y-3 text-sm text-foreground">
            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">1</span>
              <span className="flex flex-wrap items-center gap-1.5 leading-7">
                Tap the <Share className="inline h-4 w-4 text-primary" /> <span className="font-medium">Share</span> button in Safari's toolbar.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">2</span>
              <span className="flex flex-wrap items-center gap-1.5 leading-7">
                Choose <SquarePlus className="inline h-4 w-4 text-primary" /> <span className="font-medium">Add to Home Screen</span>.
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">3</span>
              <span className="flex flex-wrap items-center gap-1.5 leading-7">
                Tap <Plus className="inline h-4 w-4 text-primary" /> <span className="font-medium">Add</span> — the Friday icon appears on your Home Screen.
              </span>
            </li>
          </ol>
          <div className="mt-2 flex justify-end">
            <Button onClick={() => setIosOpen(false)} className="bg-primary text-primary-foreground hover:bg-[hsl(var(--primary-hover))]" data-testid="pwa-ios-done">
              Got it
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
